import type { ButtonProps } from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import type { PermissionUtils } from '@tyro/api';
import { Link, type LinkProps } from 'react-router';

export interface MenuItemConfig {
  label: string;
  icon?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  navigateTo?: LinkProps['to'];
  state?: LinkProps['state'];
  onBeforeNavigate?: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
  hasAccess?: (permissions: PermissionUtils) => boolean;
  isDelete?: boolean;
  'aria-expanded'?: ButtonProps['aria-expanded'];
  'aria-controls'?: ButtonProps['aria-controls'];
}

interface MenuItemListProps {
  menuItems: MenuItemConfig[];
  onClose: () => void;
  permissions: PermissionUtils;
}

export const ActionMenuIconWrapper = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: 6,
  marginLeft: -2,
  display: 'inline-flex',
  alignItems: 'center',
  '& svg': {
    width: 20,
    height: 20,
  },
}));

export function getMenuItemList({
  menuItems,
  permissions,
  onClose,
}: MenuItemListProps) {
  return menuItems
    .filter((menuItem) => menuItem.hasAccess?.(permissions) ?? true)
    .map(
      ({
        label,
        icon,
        navigateTo,
        state,
        onBeforeNavigate,
        onClick,
        disabled,
        disabledTooltip,
        isDelete,
        'aria-expanded': ariaExpanded,
        'aria-controls': ariaControls,
      }) => {
        const Item = (
          <MenuItem
            key={label}
            {...(navigateTo
              ? {
                  component: Link,
                  to: navigateTo,
                  state,
                  onClick: onBeforeNavigate,
                }
              : {
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClick?.(e as React.MouseEvent<HTMLButtonElement>);
                    onClose();
                  },
                  'aria-expanded': ariaExpanded,
                  'aria-controls': ariaControls,
                })}
            disabled={disabled}
            title={disabled ? disabledTooltip : undefined}
            sx={({ palette }) => ({
              fontSize: '0.875rem',
              ...(isDelete
                ? {
                    '&.Mui-selected': {
                      backgroundColor: palette.rose[500_16],
                    },
                    '&.Mui-selected:hover, &:hover, &.Mui-focusVisible, &:focus':
                      {
                        backgroundColor: palette.rose[500_8],
                        color: palette.rose[600],
                        '& span': {
                          color: palette.rose[600],
                        },
                      },
                    '& .MuiTouchRipple-child': {
                      backgroundColor: palette.rose[500],
                    },
                  }
                : {}),
            })}
          >
            {icon && <ActionMenuIconWrapper>{icon}</ActionMenuIconWrapper>}{' '}
            {label}
          </MenuItem>
        );

        return disabledTooltip && disabled ? (
          <Tooltip
            describeChild
            key={label}
            title={disabledTooltip}
            disableInteractive
          >
            <span>{Item}</span>
          </Tooltip>
        ) : (
          Item
        );
      },
    );
}
